import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type { IgnoreAPInvoicesInput, IgnoreAPInvoicesResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';
import { invalidateTaskQueries } from '@/api/rpc/hooks/tasks/invalidateTaskQueries';
import { buildIgnoreResultToast } from './ignoreInvoiceToasts';

export interface UseIgnoreAPInvoicesOptions {
  onSuccess?: (
    data: IgnoreAPInvoicesResponse,
    variables: IgnoreAPInvoicesInput,
  ) => void;
}

export function useIgnoreAPInvoices(options?: UseIgnoreAPInvoicesOptions) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [
    orpc.accountsPayable.invoices.list.queryKey({ input: {} }),
    orpc.accountsPayable.invoices.get.key(),
  ]);
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation(
    orpc.accountsPayable.invoices.ignore.mutationOptions({
      onSuccess: (data, variables) => {
        showToast(buildIgnoreResultToast(data));
        invalidateRpcQueryFn({});
        invalidateTaskQueries(queryClient);
        options?.onSuccess?.(data, variables);
      },
      onError: (error) => {
        showToast({
          title: 'Error',
          message: error.message || 'Failed to ignore invoices',
          color: 'error',
        });
      },
    }),
  );
}
